import { ridSegment } from '@krp/client-runtime'

/**
 * Адреса продажи и оплаты Resource.Aqsi (C3b, `AqsiSaleEndpoints`) через
 * Gateway. rid — сегмент пути каждого запроса (D-064).
 */
const resource = (resourceId: string) => `/api/aqsi/v1/resources/${ridSegment(resourceId)}`
const segment = (value: string) => encodeURIComponent(value)
const sale = (resourceId: string, saleId: string) => `${resource(resourceId)}/sales/${segment(saleId)}`
const operation = (resourceId: string, operationId: string) =>
  `${resource(resourceId)}/operations/${segment(operationId)}`

export const AQSI_SALE_URLS = {
  /** RP-FB35 K7: продажи ресурса с предупреждением службы (весь ресурс, свежие первыми). */
  /** RP-FB35 K9: отложенные продажи ресурса и их число; «Отложить» и «Вернуть на кассу». */
  parked: (resourceId: string) => `${resource(resourceId)}/sales/parked`,
  parkedCount: (resourceId: string) => `${resource(resourceId)}/sales/parked/count`,
  park: (resourceId: string, saleId: string) => `${sale(resourceId, saleId)}/park`,
  unpark: (resourceId: string, saleId: string) => `${sale(resourceId, saleId)}/unpark`,
  attention: (resourceId: string) => `${resource(resourceId)}/sales/attention`,
  sales: (resourceId: string) => `${resource(resourceId)}/sales`,
  active: (resourceId: string) => `${resource(resourceId)}/sales/active`,
  sale,
  lines: (resourceId: string, saleId: string) => `${sale(resourceId, saleId)}/lines`,
  fiscalProfile: (resourceId: string, saleId: string) => `${sale(resourceId, saleId)}/fiscal-profile`,
  /** С2: проверка корзины до оплаты под правом кассира. */
  check: (resourceId: string, saleId: string) => `${sale(resourceId, saleId)}/check`,
  pay: (resourceId: string, saleId: string) => `${sale(resourceId, saleId)}/pay`,
  fiscalize: (resourceId: string, saleId: string) => `${sale(resourceId, saleId)}/fiscalize`,
  /** RP-FB35 K3: «чека нет — пробить заново» — новая попытка чека по слову человека. */
  receiptRetry: (resourceId: string, saleId: string) => `${sale(resourceId, saleId)}/receipt/retry`,
  cancel: (resourceId: string, saleId: string) => `${sale(resourceId, saleId)}/cancel`,
  /** «Отменить» идущую оплату картой на терминале: служба просит кабинет снять операцию и читает итог. */
  cancelPayment: (resourceId: string, saleId: string) => `${sale(resourceId, saleId)}/cancel-payment`,
  /** Слип покупки нашёлся после отмены оплаты: «Оплаты не было» / «Оплата прошла — пробить чек». */
  latePayment: (resourceId: string, saleId: string) => `${sale(resourceId, saleId)}/late-payment`,
  // Привязать уже пробитый чек мимо программы: служба находит его в журнале чеков и закрывает продажу.
  issuedOutside: (resourceId: string, saleId: string) => `${sale(resourceId, saleId)}/receipt-issued-outside`,
  // UI-RMK: отложенные чеки кассы места.
  heldCarts: (resourceId: string) => `${resource(resourceId)}/held-carts`,
  hold: (resourceId: string, saleId: string) => `${sale(resourceId, saleId)}/hold`,
  heldRestore: (resourceId: string, heldCartId: string) => `${resource(resourceId)}/held-carts/${segment(heldCartId)}/restore`,
  heldDrop: (resourceId: string, heldCartId: string) => `${resource(resourceId)}/held-carts/${segment(heldCartId)}/drop`,
  operation,
  reconcile: (resourceId: string, operationId: string) => `${operation(resourceId, operationId)}/reconcile`,
  resolve: (resourceId: string, operationId: string) => `${operation(resourceId, operationId)}/resolve`,
} as const

/**
 * Поиск РМК - в общем каталоге Platform, тем же поиском, что окно «Номенклатура» (штрихкод и артикул точно из базы, текст - единая служба
 * text-search). Право - `session.open` на ресурсе-кассе. Литерал `/api/` законен только здесь.
 */
export const PLATFORM_SALE_SEARCH_URLS = {
  /** `GET ?resourceId=&q=&mode=text|barcode&limit=`. */
  search: '/api/platform/v1/catalog/sale-search',
} as const

/**
 * «Отправить в кассу сейчас» и «почему товара нет в кассе» — служба синхронизации (RP-SYN13): создание карточки в кассе из общего каталога
 * задачей применения. Право — то же `session.open` на кассе, что у поиска.
 */
export const SYNC_CASH_DELIVERY_URLS = {
  /** `POST { operationId, resourceId, productId }` → ход отправки. */
  deliver: '/api/sync/v1/cash-delivery',
  /** `GET` — ход отправки по задаче. */
  job: (jobId: string) => `/api/sync/v1/cash-delivery/${encodeURIComponent(jobId)}`,
  /** `POST { resourceId, productIds }` → почему товаров нет в кассе (идёт отправка или отказ кассы дословно). */
  reasons: '/api/sync/v1/cash-delivery/reasons',
} as const
